import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { isNotNullOrUndefined } from "@pcp/core/util";
import {
    ActiveElementUiStateService,
    ClassroomFacade,
    fromNavigationPreview,
    SyncedLessonState,
} from "shared/classroom/data-access";
import { ClassroomEvents } from "shared/classroom/data-access";
import { EMPTY } from "rxjs";
import { filter, map, skip, switchMap, tap, withLatestFrom } from "rxjs/operators";
import * as InternalPreviewScreenActions from "../internal/internal-preview-screen.actions";
import { NavigationPreviewStore } from "../preview/next-slides-preview/navigation-preview.store";

const SLIDE_KEYS = [
    "primarySlide",
    "secondarySlide",
    "tertiarySlide",
] as const satisfies readonly (keyof SyncedLessonState)[];

@Injectable()
export class PreviewScreenEffects {
    private readonly uiState = inject(ActiveElementUiStateService);
    private readonly classroomFacade = inject(ClassroomFacade);
    private readonly store = inject(Store);
    private readonly navigationPreview = inject(NavigationPreviewStore);

    /**
     * User has clicked on the "show previous bookpage" link from inside the preview screen.
     * Show it inside the preview screen as a secondary slide.
     */
    readonly remap_showPreviousBookpageLinkHasBeenClicked$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    InternalPreviewScreenActions.showPreviousBookpageLinkHasBeenClicked
                ),
                map(() => this.uiState.bookpageSignals.previousBookpageAsDisplaySlide()),
                filter(isNotNullOrUndefined),
                tap((slide) => this.classroomFacade.showSlide(2, slide))
            ),
        { dispatch: false }
    );

    /**
     * User has clicked on the "show next bookpage" link from inside the preview screen.
     * Show it inside the preview screen as a secondary slide.
     */
    readonly remap_showNextBookpageLinkHasBeenClicked$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    InternalPreviewScreenActions.showNextBookpageLinkHasBeenClicked
                ),
                map(() => this.uiState.bookpageSignals.nextBookpageAsDisplaySlide()),
                filter(isNotNullOrUndefined),
                tap((slide) => this.classroomFacade.showSlide(2, slide))
            ),
        { dispatch: false }
    );

    /**
     * Capture the most recent hotspot click into the latch service. Consumed by
     * `ClassroomFacade.showSlide` when the hotspot's target slide is shown.
     */
    readonly latchHotspotOnUserActivation$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ClassroomEvents.hotspotActivated),
                tap(({ hotspot }) => this.classroomFacade.latchHotspot(hotspot))
            ),
        { dispatch: false }
    );

    /**
     * Hide this client's navigation preview whenever a slide is written to any layer - also
     * when the same slide is shown again, or the `SlideType.empty` sentinel clears the beamer.
     *
     * Runs on every client that registers this feature shell, so every connected beamer closes
     * its preview. The slide is written by one client only (usually the teacher, e.g. after a
     * beamer thumb click intent, or when the teacher navigates), so the hide cannot live in the
     * write path (`ClassroomFacade.showSlide`) - the beamer would never run it (#33829).
     *
     * Uses `changes$`, not `toObservable(slideForLayer(n))`: on every hydration edge (page load /
     * late join, lesson start and stop) `changes$` emits ONE snapshot with the restored slide
     * first. `skip(1)` per hydration edge keeps an open preview open in that case. A shown
     * slide is a JSON string; a closed layer is `null` and does not hide the preview.
     *
     * No `distinctUntilChanged()` on `hydrated$`: `changes$` re-subscribes (and re-emits its
     * snapshot) on every `hydrated$` emission, so this effect must restart and skip on each one too.
     */
    readonly hideNavigationPreviewOnSlideShown$ = createEffect(
        () =>
            this.classroomFacade.hydrated$.pipe(
                switchMap((hydrated) =>
                    hydrated ? this.classroomFacade.changes$.pipe(skip(1)) : EMPTY
                ),
                filter((changes) => SLIDE_KEYS.some((key) => typeof changes[key] === "string")),
                tap(() => this.navigationPreview.hide())
            ),
        { dispatch: false }
    );

    /**
     * Re-seed this client's navigation preview indices from the teacher's synced slide context
     * (Y.Doc `navigationPreviewNextSlides`, mirrored into NgRx): the basic preview starts one after
     * the current basic slide (or at 0 when basic slides exist but no index is set), the extension
     * preview at the stored extension index.
     *
     * Gated on `hydrated$` so it also runs pre-lesson (offline draft Y.Doc). Reads the STORED data,
     * not the `setNavigationPreviewNextSlides` action: after a page reload that action is dispatched
     * from the hydration snapshot before the gate opens, so an `ofType` listener missed it (#33667).
     * A store `select` gives the current value when the gate opens. It emits again only when the
     * reducer stores a new `data` object, so unrelated store changes do not reset the user's
     * preview position.
     */
    readonly onNavigationPreviewNextSlidesChanged$ = createEffect(
        () =>
            this.classroomFacade.hydrated$.pipe(
                switchMap((hydrated) =>
                    hydrated
                        ? this.store.select(fromNavigationPreview.selectNavigationPreviewNextSlideData)
                        : EMPTY
                ),
                withLatestFrom(this.store.select(fromNavigationPreview.selectBasicSlideList)),
                tap(([data, basicSlideList]) => {
                    const basicSlideIdx = data?.basicSlideIdx;
                    let basicSlidePreviewIdx: number | undefined;
                    if (basicSlideIdx !== undefined) {
                        basicSlidePreviewIdx = basicSlideIdx + 1;
                    } else if (basicSlideList.length > 0) {
                        basicSlidePreviewIdx = 0;
                    }

                    this.navigationPreview.resetFromYjs({
                        basicSlidePreviewIdx,
                        extensionSlidePreviewIdx: data?.extensionSlideIdx ?? 0,
                        extensionSlides: data?.extensionSlides ?? [],
                    });
                })
            ),
        { dispatch: false }
    );

    // Scroll-video scrubbing no longer flows through here (`canvas-scroll-video-slides`): the
    // fabric container syncs a frame INDEX (ephemeral live stream + settled
    // `<layer>SlideScrollVideoFrame`) instead of rebuilding a slide per wheel tick.

    constructor(private readonly actions$: Actions) {}
}
